import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TableRowPropsT } from "@interfaces/departure-schedule";
import { $UserAddPermissions, ACTIONS, UserPriorityEnum } from "@store/user-store";
import DepartureScheduleService from "@services/departure-schedule-service";
import clsx from "clsx";
import style from "@scss/pages/company/departure-schedule.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { ScrollArea } from "@mantine/core";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import { ArrowDropDownGraySVG } from "@components/svgs/arrow-drop-down-gray";
import {
  NotePencilIcon,
  TrashIcon,
  CalendarCheckIcon,
  CarProfileIcon,
  CaretLeftIcon,
  CaretRightIcon,
  DownloadSimpleIcon,
  CheckCircleIcon,
  XCircleIcon
} from "@phosphor-icons/react";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Typography,
  ListItemText,
} from "@mui/material";
import { useUnit } from "effector-react";
import { useBoolean } from "@hooks/useBoolean";
import { downloadBlob } from "@utils/downloadBlob";
import moment from "moment";

const DepartureScheduleTableRow: React.FC<TableRowPropsT> = (props) => {
  const permissions = useUnit($UserAddPermissions);
  const {
    item,
    isUser,
    onAddHistory,
    onRemoveHistoryItem,
    onEditHistoryItem,
    onAccepted,
  } = props;
  const collapse = useBoolean();
  const tableRef = useRef<HTMLTableElement>(null);
  const intervalScroll = useRef<ReturnType<typeof setInterval>>(null);
  const [scrollActive, setScrollActive] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"right" | "left">("right");

  useEffect(() => {
    if(scrollActive && scrollDirection){
      intervalScroll.current = setInterval(() => {
        switch(scrollDirection){
          case "left":
            tableRef.current?.scrollBy({
              left: -20,
              behavior: 'smooth'
            })
            break;
          case "right":
            tableRef.current?.scrollBy({
              left: 20,
              behavior: 'smooth'
            })
            break;
        }
      }, 10);
    }else{
      if (intervalScroll.current) clearInterval(intervalScroll.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollActive, scrollDirection])

  const handleScrollTablePeriod = (direction: "right" | "left") => {
    setScrollDirection(direction);
    setScrollActive(true);
  }

  const handleDownloadFile = (id: number, name: string) => {
    DepartureScheduleService.getFile(id, (err, res) => {
      if (err || !res) return;

      downloadBlob(res.data, name);
    })
  };

  let colSpan = 7;
  if (!isUser) {
    colSpan = 6;
  }

  return (
    <>
      <TableRow className={clsx(style.departure_schedule_row)}>
        <TableCell sx={{ px: 1, whiteSpace: "nowrap", textAlign: "center", maxWidth: '5%' }}>
          <Tooltip title="Запланировать выезд" placement="top">
            <IconButton
              onClick={(e) => {
                if (onAddHistory) onAddHistory("planned", item);
              }}
              className={clsx(style.departure_schedule_row_collapseIcon)}
            >
              <WrapperSVG color="blue">
                <CarProfileIcon size={24} />
              </WrapperSVG>
            </IconButton>
          </Tooltip>
          {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToCreateHistory) && (
            <Tooltip title="Добавить выезд" placement="top">
              <IconButton
                onClick={(e) => {
                  if (onAddHistory) onAddHistory("full", item);
                }}
                className={clsx(style.departure_schedule_row_collapseIcon)}
              >
                <WrapperSVG color="green">
                  <CalendarCheckIcon size={24} />
                </WrapperSVG>
              </IconButton>
            </Tooltip>
          )}
          {item.history && item.history.length > 0 && (
            <Tooltip title="Посмотреть историю" placement="top">
              <IconButton
                color={collapse.value ? "inherit" : "default"}
                onClick={collapse.onToggle}
                sx={{
                  ...(collapse.value && {
                    bgcolor: "action.hover",
                  }),
                  padding: 2,
                }}
                className={clsx(style.departure_schedule_row_collapseIcon)}
              >
                <WrapperSVG color="gray">
                  <ArrowDropDownGraySVG
                    className={clsx({
                      [style.departure_schedule_row_collapseIcon_rotated]: collapse.value,
                    })}
                  />
                </WrapperSVG>
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        {isUser && <TableCell>{item.user.name}</TableCell>}
        <TableCell>{item.company.name}</TableCell>
        <TableCell>{item.address}</TableCell>
        <TableCell sx={{ whiteSpace: 'pre-line', maxHeight: '100px' }}>
          <ScrollArea style={{ height: "100px", maxHeight: "100%" }}>
          {item.contacts.map((e, i) => (
            <>
              <ListItemText
                primary={e.name}
                secondary={`${e.email} ${e.phone}`}
              />
              {i < item.contacts.length - 1 && (
                <Divider orientation="horizontal" sx={{ borderStyle: 'dashed' }} />
              )}
            </>
          ))}
          </ScrollArea>
        </TableCell>
        <TableCell>{item.count_departure.title}</TableCell>
        <TableCell sx={{ padding: 0, height: '1px', position: 'relative', overflow: 'hidden' }}>
          <CaretLeftIcon
            className={clsx(style.departure_schedule_row_left)}
            onMouseDown={() => handleScrollTablePeriod('left')}
            onMouseUp={() => setScrollActive(false)}
            onMouseLeave={() => setScrollActive(false)}
            size={24}
          />
          <TableContainer
            sx={{ minHeight: '100%', display: 'flex' }}
            ref={tableRef}
          >
            <Table sx={{ width: 'max-content', tableLayout: 'fixed' }}>
              <TableRow className={clsx(style.departure_schedule_row_periods)}>
              {item.periods.map((period, i) => (
                <TableCell
                  className={clsx({
                    [style.departure_schedule_row_success_light]: period.dates.filter(d => d.type === "success").length > 0,
                    [style.departure_schedule_row_danger]: period.status === "danger",
                    // [style.departure_schedule_row_not_departure]: period.status === "not_departure",
                    [style.departure_schedule_row_system]: period.status !== "danger" && period.dates.filter(d => d.type === "system").length > 0,
                    [style.departure_schedule_row_planned]: period.dates.filter(d => d.type === "planned").length > 0,
                    [style.departure_schedule_row_warning]: period.dates.filter(d => d.type === "warning").length > 0,
                  })}
                >
                  <Typography variant="body2" textAlign="center" className={clsx(style.departure_schedule_row_month)}>
                    {moment().startOf('year').add(i, 'month').format('MMM')}
                  </Typography>
                  {period.dates.length > 0 && period.dates.map(p => (
                    <Chip
                      label={p.date}
                      size="small"
                      variant="outlined"
                      sx={{ paddingTop: '2px', mb: 0.2 }}
                      className={
                        clsx({
                          [style.departure_schedule_day_success]: p.type === "success",
                          [style.departure_schedule_day_planned]: p.type === "planned",
                          [style.departure_schedule_day_warning]: p.type === "warning",
                          [style.departure_schedule_day_system]: p.type === "system",
                        })
                      }
                    />
                  ))}
                </TableCell>
              ))}
              </TableRow>
            </Table>
          </TableContainer>
          <CaretRightIcon
            className={clsx(style.departure_schedule_row_right)}
            onMouseDown={() => handleScrollTablePeriod('right')}
            onMouseUp={() => setScrollActive(false)}
            onMouseLeave={() => setScrollActive(false)}
            size={24}
          />
        </TableCell>
      </TableRow>
      {item.history && item.history.length > 0 && (
        <TableRow>
          <TableCell sx={{ p: 0, border: "none" }} colSpan={colSpan}>
            <Collapse
              in={collapse.value}
              timeout="auto"
              unmountOnExit
              sx={{ bgcolor: "background.neutral" }}
            >
              <Stack
                component={Paper}
                className={clsx(style.courses_depth)}
                sx={{
                  m: 0.5,
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  boxShadow: "0px 2px 1px -1px #bcf5dd, 0px 1px 1px 0px #bcf5dd, 0px 1px 3px 0px #bcf5dd",
                }}
                direction="row"
                justifyContent="space-between"
              >
                {item.history && item.history.length > 0 && (
                  <TableContainer
                    className={clsx(
                      tableStyle.base_table_container,
                      style.courses_table
                    )}
                    sx={{ position: "relative", width: "100%", maxWidth: "100%" }}
                  >
                    <Table className={clsx(tableStyle.base_table)}>
                      <TableBody>
                        {item.history.map((history) => (
                          <TableRow className={clsx({
                            [style.departure_schedule_row_success_light]: history.isAccepted,
                            [style.departure_schedule_row_warning_light]: !history.isAccepted && (history.comment !== null || history.file !== null || history.prescriptionId !== null),
                          })}>
                            {history.comment === null && history.file === null && history.prescriptionId === null && (
                              <TableCell sx={{ whiteSpace: "nowrap" }} colSpan={6 - (item.history.filter(e => e.comment !== null || e.file !== null || e.prescriptionId !== null).length > 0 ? 2 : 0)}>
                                {history.user.name} запланировал(а) выезд на {moment(history.date).format("D MMM YYYY г.")}
                              </TableCell>
                            )}
                            {(history.comment !== null || history.file !== null || history.prescriptionId !== null) && (
                              <>
                                <TableCell>Сотрудник: <b>{history.user.name}</b></TableCell>
                                {history.date ? (
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Дата: <b>{moment(history.date).format("D MMM YYYYг.")}</b></TableCell>
                                ) : (
                                  <TableCell>Дата: <b>Не указано</b></TableCell>
                                )}
                                {history.comment ? (
                                  <TableCell className={clsx(style.departure_schedule_row_comment)}>Примечание: <b>{history.comment}</b></TableCell>
                                ) : (
                                  <TableCell>Примечание: <b>Не указано</b></TableCell>
                                )}
                                {history.prescriptionId && (
                                  <TableCell>
                                    <Link 
                                      to={`/company/prescriptions/${history.prescriptionId}`}
                                      className={clsx("green-colorful-btn")}
                                      style={{ padding: '3px 5px' }}
                                    >
                                      Предписание #{history.prescriptionId}/{history.companyId}
                                    </Link>
                                    &nbsp;({moment(history.prescription?.createdAt).format('DD.MM.YYг.')})
                                  </TableCell>
                                )}
                              </>
                            )}
                            <TableCell sx={{ textAlign: 'right' }}>
                              {history.file && (
                                <Tooltip title="Скачать" placement="top">
                                  <button onClick={() => handleDownloadFile(history.id, history.file!)}>
                                    <WrapperSVG color="green-light" margin>
                                      <DownloadSimpleIcon size={20} />
                                    </WrapperSVG>
                                  </button>
                                </Tooltip>
                              )}
                              {(permissions.hasRequiredLevel(UserPriorityEnum.Owner) || permissions.hasRequiredLevel(UserPriorityEnum.SuperAdmin)) &&
                                (history.comment !== null || history.file !== null || history.prescriptionId !== null) && (
                                <>
                                {history.isAccepted ? (
                                  <Tooltip title="Отменить подтверждение" placement="top">
                                    <button onClick={() => onAccepted(false, history.id)}>
                                      <WrapperSVG color="red" margin>
                                        <XCircleIcon size={20} />
                                      </WrapperSVG>
                                    </button>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Подтвердить" placement="top">
                                    <button onClick={() => onAccepted(true, history.id)}>
                                      <WrapperSVG color="green" margin>
                                        <CheckCircleIcon size={20} weight="bold" />
                                      </WrapperSVG>
                                    </button>
                                  </Tooltip>
                                )}
                                </>
                              )}

                              {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToEditHistory) &&
                                history.prescriptionId !== null && (
                                <Tooltip title="Изменить" placement="top">
                                  <button
                                    onClick={(e) => {
                                      if (onEditHistoryItem) onEditHistoryItem(history);
                                    }}
                                  >
                                    <WrapperSVG color="orange" margin>
                                      <NotePencilIcon size={24} />
                                    </WrapperSVG>
                                  </button>
                                </Tooltip>
                              )}
                              {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToDeleteHistory) && (
                                <Tooltip title="Удалить" placement="top">
                                  <button
                                    onClick={(e) => {
                                      if (onRemoveHistoryItem) onRemoveHistoryItem(history.id);
                                    }}
                                  >
                                    <WrapperSVG color="" margin>
                                      <TrashIcon size={24} />
                                    </WrapperSVG>
                                  </button>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default DepartureScheduleTableRow;
